import React, {useMemo} from "react";
import moment from "moment";
import { DateFormats } from "../../components/constants/securspace-constants";
import { PersonAndBoxMessage } from "../../components/messages/PersonAndBoxMessage";
import { useSnackbar } from "../../context/snackbar-context";
import { useSnackbarDisplayOnError } from "../../hooks/error/useSnackbarDisplayOnError";
import { useBindSearchQuery } from "../../hooks/useBindSearchQuery";
import { useGetBoundReportPaginated } from "../../hooks/request/useGetBoundReportPaginated";
import { BoundTablePagination } from "../../components/common/inputs/boundInputs";
import { InvoiceTransactionVO } from "../../types/model/valueObject/invoice/InvoiceTransactionVO";
import { SupplierInvoicesSortFilter, SupplierInvoicesSearchParams } from "../../components/supplier/invoicesReport/SupplierInvoicesSortFilter";
import { SUPPLIER_INVOICES_DATE_BYPASS_KEYS } from "../../types/model/criteria/invoice/SupplierInvoiceSearchCriteria";
import { withDateRangeBypass } from "../../util/ReportDateRangeUtils";
import { SupplierInvoicesCardListWithLoader } from "../../components/supplier/invoicesReport/SupplierInvoicesCardList";
import { supplierInvoicesReportEndpoints as reportEndpoints } from "../../requests/api-endpoints";
import { useReportDataSummaryCollapseByIndex}  from "../../hooks/report/useReportDataSummaryCollapseByIndex";
import { useBoundQueryDownloadUrl } from "../../hooks/report/useBoundQueryDownloadUrl";
import DownloadCSVButton from "../../components/report/export/DownloadCSVButton";
import {
  DownloadButtonsContainer,
  ReportDataSummary,
} from "../../components/report/redesigned";
import {
  SecurSpaceContainer,
  SecurSpaceReportContainer,
  SecurSpaceReportTitle
} from "../../components/common";

// a default query MUST be defined outside of any components to avoid re-initialization on each render
const DEFAULT_QUERY: SupplierInvoicesSearchParams = {
  startDate: moment().subtract(1, 'month').format(DateFormats.DAY),
  endDate: moment().format(DateFormats.DAY),
  bookingNumber: '',
  invoiceNumber: '',
  locationId: '',
  sortBy: "TRANSACTION_NUMBER",
  sortDir: 'desc',
  page: 0,
  size: 25,
};

// Links built with the API's field names (e.g. from the assistant) fill the visible filters.
const QUERY_PARAM_RENAMES: Record<string, string> = {
  transactionNumber: 'invoiceNumber',
  orderNumber: 'bookingNumber',
};

const SupplierInvoicesReport = () => {
  const { snackbarShowMessage } = useSnackbar();
  // state hooks
  const [reportQuery, setReportQuery] = useBindSearchQuery(DEFAULT_QUERY, { paramRenames: QUERY_PARAM_RENAMES });
  const requestQuery = useMemo(() => withDateRangeBypass(reportQuery, SUPPLIER_INVOICES_DATE_BYPASS_KEYS), [reportQuery]);

  // Api hooks
  const {
    reportPayload,
    pageInfo,
    isLoading,
    error
  } =  useGetBoundReportPaginated<InvoiceTransactionVO[]>({
    requestUrl: reportEndpoints.report,
    requestParams: requestQuery
  });

  const downloadUrl = useBoundQueryDownloadUrl({ endpoint: reportEndpoints.download, query: requestQuery });

  const {
    collapseByIndex: expandByIndex, // TODO yf when redesigns slow down, rename this
    handleToggleCollapse: handleToggleExpand, // TODO yf same as above
    hasExpandedItems,
    setExpandedItems,
    setExpandedAll,
    handleExpandAll,
  } = useReportDataSummaryCollapseByIndex({ data: reportPayload });

  // error messaging
  useSnackbarDisplayOnError({ error, snackbarShowMessage });

  // conditional display hooks
  // Not gated on `isLoading`: the previous payload survives a refetch, so
  // leaving this on keeps the empty state put instead of blinking out and
  // back on every filter change. It is undefined until the first load, so it
  // cannot flash before there is an answer.
  const shouldShowPersonAndBox = useMemo(() => {
    return !error && reportPayload?.length === 0
  }, [error, reportPayload]);

  const showPagination = useMemo(() => {
    return !!(reportPayload?.length && reportPayload.length > 0);
  }, [reportPayload])

  // change handlers
  const handleFilterChange = (newQuery: SupplierInvoicesSearchParams) => {
    const updatedQuery = {
      ...reportQuery,
      ...newQuery
    };
    if (!newQuery.page) {
      // reset to first page on filter change if it is not being explicitly set
      updatedQuery.page = 0;
    }

    setReportQuery(updatedQuery);
  }

  return (
    <SecurSpaceContainer>
      <SecurSpaceReportContainer>
        <SecurSpaceReportTitle>Invoices</SecurSpaceReportTitle>
        <SupplierInvoicesSortFilter
            query={reportQuery}
            onChange={handleFilterChange}
          />
        <ReportDataSummary
          visible={!shouldShowPersonAndBox}
          count={reportPayload?.length || 0}
          title={"All"}
          isShowCollapseButton={true}
          hasExpandedItems={hasExpandedItems}
          setExpandedItems={setExpandedItems}
          setExpandedAll={setExpandedAll}
          handleExpandAll={handleExpandAll}
          downloadButtonsSlot={
            <DownloadButtonsContainer>
              <DownloadCSVButton title='Download Report' reportUrl={downloadUrl} />
            </DownloadButtonsContainer>
          }
        />
        <SupplierInvoicesCardListWithLoader
          visible={!error}
          isLoading={isLoading}
          items={reportPayload}
          expandByIndex={expandByIndex}
          onToggleExpand={handleToggleExpand}
        />
        <PersonAndBoxMessage
          visible={shouldShowPersonAndBox }
        />
        <BoundTablePagination<SupplierInvoicesSearchParams>
          visible={showPagination}
          pageInfo={pageInfo}
          onChange={handleFilterChange}
        />
      </SecurSpaceReportContainer>
    </SecurSpaceContainer>
  );
}

export default SupplierInvoicesReport;
